/**
 * Собирает presentation общей таблицы: форматирует значения и выбирает
 * компонент сложной ячейки. Модуль не загружает данные; готовые справочные
 * stores используются только для подписей и цветов существующих значений.
 */
import { formatDateAndTime, formatDate } from "@/utils/dateUtils";
import { getContrastColor } from "@/utils/colorUtils";
import edoDocumentsColumns from "@/config/table-columns/edo-documents.json";

import { useFleetRepairStore } from "@/stores/fleetRepairStore";
import { useOrderStore } from "@/stores/orderStore";
import { useTripStore } from "@/stores/tripStore";
import { useVehicleStore } from "@/stores/vehicleStore";
import { useVehicleDocumentStore } from "@/stores/vehicleDocumentStore";
import { usePermissionRolesStore } from "@/stores/permissionRolesStore";

const numberFormatterRu = new Intl.NumberFormat("ru-RU", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  trailingZeroDisplay: "stripIfInteger",
});

const baseFormatters = {
  text: (value) => value || "—",
  number: (value) => (value != null ? value.toString() : "—"),

  big_number: (value) => {
    if (value === null || value === undefined || value === "")
      return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return "—";
    return numberFormatterRu.format(num);
  },

  badge: (value, row, config) => {
    if (!value) return "—";
    if (config.colorMap && config.labels) {
      const color = config.colorMap[value] || "#6c757d";
      const displayText = config.labels[value] || value;
      return createBadge(value, color, displayText);
    }
    return createBadge(value, "#6c757d", value);
  },

  status: (value) => {
    const color = value == true ? "#16a34a" : "var(--color-danger)";
    const displayText = value == true ? "Включен" : "Отключен";
    return createBadge(value, color, displayText);
  },

  date: (value) => {
    if (!value) return "—";
    return formatDate(value);
  },

  datetime: (value) => {
    if (!value) return "—";
    return formatDateAndTime(value);
  },

  array: (value) => {
    if (!value || !Array.isArray(value)) return "—";
    if (value.length === 0) return "—";
    return value
      .map(
        (item) =>
          `<span style="display: inline-block; padding: 6px; margin-bottom: 10px; width: max-content; background: #f0f2f5; border: 1px solid #dfe1e5; border-radius: 6px; font-size: 13px; line-height: 1.2;">${item}</span>`
      )
      .join("");
  },

  computed: {
    fullName: (value, row) => {
      const fullName = [
        row.last_name,
        row.first_name,
        row.middle_name,
      ]
        .filter(Boolean)
        .join(" ");
      return fullName || "—";
    },

    tripDrivers: (value, row) => {
      if (!row.drivers || !Array.isArray(row.drivers)) return "—";
      if (row.drivers.length === 0) return "—";
      return row.drivers.map((driver) => driverEntityLink(driver));
    },

    tripVehicles: (value, row) => {
      if (!row.vehicles || !Array.isArray(row.vehicles)) return "—";
      if (row.vehicles.length === 0) return "—";
      return row.vehicles.map((vehicle) => vehicleEntityLink(vehicle));
    },

    vehicleObject: (value, row) => {
      const vehicle = value;
      if (!vehicle || typeof vehicle !== "object") return "—";
      return vehicleEntityLink(vehicle);
    },

    vehicleObjects: (value, row) => {
      const vehicles = value;
      if (!vehicles || !Array.isArray(vehicles)) return "—";
      if (vehicles.length === 0) return "—";
      return vehicles.map((vehicle) => vehicleEntityLink(vehicle));
    },

    userPhone: (value, row) => {
      const phone = row.user?.phone;
      return phone || "—";
    },

    userEmail: (value, row) => {
      const email = row.user?.email;
      return email || "—";
    },

    expenseCategory: (value, row) => {
      const category = `${row.expense_category?.emoji} ${row.expense_category?.name}`;
      return category || "—";
    },

    expenseStatus: (value, row) => {
      const isAccepted = row.accepter_id != null;
      if (isAccepted) {
        const accepterName = row.accepter_initials || "Подтверждено";
        return `<div style="display: flex; align-items: center; gap: 6px; font-size: 14px;"><svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 48 48" fill="#4caf50"><path fill="#c8e6c9" d="M44,24c0,11.045-8.955,20-20,20S4,35.045,4,24S12.955,4,24,4S44,12.955,44,24z"/><path d="M34.586,14.586l-13.57,13.586l-5.602-5.586l-2.828,2.828l8.434,8.414l16.395-16.414L34.586,14.586z"/></svg><span>${accepterName}</span></div>`;
      } else {
        return `<button type="button" class="expense-accept-btn" data-expense-id="${row.id}" style="padding: 6px 12px; background: var(--color-green); color: white; border: 1px solid var(--color-dark); border-radius: 4px; cursor: pointer; font-size: 14px; white-space: nowrap; transition: all 0.2s ease;" onmouseover="this.style.background='var(--color-green-hover)'" onmouseout="this.style.background='var(--color-green)'">Подтвердить</button>`;
      }
    },

    repairInspectionStatus: (value, row) => {
      const isInspected = row.inspector_id != null || row.inspected_at != null;
      const inspectorName = isInspected
        ? (
          row.inspector?.full_name ||
          row.inspector?.name ||
          row.inspector?.phone ||
          "Подписано"
        )
        : "Подписать";

      return {
        component: "FleetRepairInspectionStatusCell",
        props: {
          repairId: row.id,
          inspected: isInspected,
          inspectorName,
        },
        text: inspectorName,
      };
    },

    // Контрагент - кликабельная ссылка
    counterpartyObject: (value, row) => {
      const counterparty = value;
      if (!counterparty || typeof counterparty !== "object")
        return "—";
      const label =
        counterparty.name || `Контрагент #${counterparty.id}`;
      return `<span class="entity-link" data-entity-type="counterparty" data-entity-id="${counterparty.id}" style="display: inline-block; padding: 6px; margin: 2px 4px 2px 0; background: #f0f2f5; border: 1px solid #dfe1e5; border-radius: 6px; font-size: 13px; line-height: 1.2; cursor: pointer; color: var(--color-blue); text-decoration: underline; text-underline-offset: 2px;">${label}</span>`;
    },

    // Источник оплаты - название + банк если есть
    paymentSourceObject: (value, row) => {
      const source = value;
      if (!source || typeof source !== "object") return "—";
      const parts = [source.name];
      if (source.bank_name) parts.push(source.bank_name);
      const label = parts.filter(Boolean).join(" · ");
      return `<span style="display: inline-block; padding: 6px; margin: 2px 4px 2px 0; background: #f0f2f5; border: 1px solid #dfe1e5; border-radius: 6px; font-size: 13px; line-height: 1.2;">${label}</span>`;
    },

    // Плательщик расхода - инициалы пользователя
    spenderObject: (value, row) => {
      const spender = value || row.spender;
      if (!spender || typeof spender !== "object") return "—";
      return spender.initials || "—";
    },

    // Тип источника расхода
    expenseSourceType: (value, row) => {
      const sourceTypeMap = {
        manual: "Создан вручную",
        bank_statement: "Импортирован из банковских выписок",
      };
      const label = sourceTypeMap[value];
      if (!label) return value || "—";
      const styles =
        value === "manual"
          ? "background: #dbeafe; color: var(--color-blue);"
          : "background-color: #d1fae5; color: #059669;";
      return `<span style="display: inline-block; padding: 6px; margin: 2px 4px 2px 0; border-radius: 6px; font-size: 13px; font-weight: 500; ${styles}">${label}</span>`;
    },
  },
};

const vehicleArrayFormatter = (value, row) => {
  if (
    Array.isArray(value) &&
    value.some((item) => item && typeof item === "object")
  ) {
    return baseFormatters.computed.vehicleObjects(value, row);
  }

  return baseFormatters.array(value);
};

const vehicleIdFormatter = (value) => {
  if (!value) return "—";
  const vehicleStore = useVehicleStore();
  const vehicle = vehicleStore.vehicles.find(
    (v) => String(v.id) === String(value)
  );
  return vehicleEntityLink(vehicle || { id: value });
};

function driverEntityLink(driver, newTab = false) {
  return {
    component: "DriverEntityLink",
    key: `driver-${driver.id}`,
    props: { driver, newTab },
    text: driver.selector_name || driver.full_name || `Водитель #${driver.id}`,
  };
}

function userEntityLink(user) {
  if (!user || typeof user !== "object" || !user.id) return "—";

  const text = user.initials || user.full_name || user.name || `Пользователь #${user.id}`;

  return {
    component: "UserEntityLink",
    key: `user-${user.id}`,
    props: { user, newTab: true },
    text,
  };
}

function vehicleEntityLink(vehicle) {
  return {
    component: "VehicleEntityLink",
    key: `vehicle-${vehicle.id}`,
    props: { vehicle },
    text: vehicle.registration_number || vehicle.selector_name || `ТС #${vehicle.id}`,
  };
}

const repairStatusFormatter = (value) => {
  const store = useFleetRepairStore();
  return createBadge(value, store.statusColor(value), store.formatStatus(value));
};

const repairItemsFormatter = (value) => {
  if (!Array.isArray(value) || value.length === 0) return "—";

  return value
    .slice(0, 3)
    .map((item) => {
      const label = escapeHtml(item.name || "Строка ремонта");
      const amount =
        item.amount === null ||
        item.amount === undefined ||
        item.amount === ""
          ? ""
          : ` · ${numberFormatterRu.format(Number(item.amount))} ₽`;

      return `<span style="display: inline-block; padding: 6px; margin: 2px 4px 2px 0; background: #f0f2f5; border: 1px solid #dfe1e5; border-radius: 6px; font-size: 13px; line-height: 1.2;">${label}${amount}</span>`;
    })
    .join("");
};

const moneyFormatter = (value) => {
  const formatted = baseFormatters.big_number(value);
  return formatted === "—" ? formatted : `${formatted} ₽`;
};

const entitySpecificFormatters = {
  orders: {
    plan: (value) => orderPlanFactCell(value, "plan"),
    fact: (value) => orderPlanFactCell(value, "fact"),
    plan_fact: (value) => orderPlanFactCell(value, "combined"),
    price_payment: (value) => orderCompositeCell(value, "OrderPricePaymentCell"),
    transport_crew: (value) => orderCompositeCell(value, "OrderTransportCrewCell"),
    status: (value) => {
      const orderStore = useOrderStore();
      const color = orderStore.orderStatusColor(value);
      const displayText = orderStore.formatOrderStatus(value);
      return createBadge(value, color, displayText);
    },
    payment_status: (value) => {
      const orderStore = useOrderStore();
      const color = orderStore.paymentStatusColor(value);
      const displayText = orderStore.formatPaymentStatus(value);
      return createBadge(value, color, displayText);
    },
    payment_form: (value) => {
      const orderStore = useOrderStore();
      return orderStore.formatPaymentForm(value);
    },
    order_type: (value, row) => {
      const type = row.order_type;
      if (!type) return "—";
      return createBadgeWithContrast(type.name, type.color);
    },
    vehicles: baseFormatters.computed.vehicleObjects,
    drivers: (value, row) => {
      if (!Array.isArray(row.drivers) || row.drivers.length === 0) return "—";

      return row.drivers.map((driver) => driverEntityLink(driver, true));
    },
    responsible_user: (value) => userEntityLink(value),
    created_by: (value) => userEntityLink(value),
  },
  trips: {
    status: (value) => {
      const tripStore = useTripStore();
      const color = tripStore.tripStatusColor(value);
      const displayText = tripStore.formatTripStatus(value);
      return createBadge(value, color, displayText);
    },
    // у рейсов vehicles — массив объектов с vehicle_type
    vehicles: baseFormatters.computed.vehicleObjects,
  },
  waybills: {
    vehicles: baseFormatters.computed.tripVehicles,
    drivers: baseFormatters.computed.tripDrivers,
  },
  vehicles: {
    vehicle_type: (value) => {
      const vehicleStore = useVehicleStore();
      return vehicleStore.formatVehicleType(value);
    },
  },
  vehicle_documents: {
    vehicle_registration_number: (value) => value || "—",
    vehicle_display_name: (value) => value || "—",
    document_kind: (value) => {
      const vehicleDocumentStore = useVehicleDocumentStore();
      return vehicleDocumentStore.formatDocumentKind(value);
    },
    ownership_type: (value) => {
      if (!value) return "—";
      const vehicleDocumentStore = useVehicleDocumentStore();
      return vehicleDocumentStore.formatOwnershipType(value);
    },
    basis_counterparty: (value) => {
      if (!value || typeof value !== "object") return "—";
      return value.name || value.inn || `Контрагент #${value.id}`;
    },
    current: (value) => (value ? "Текущий" : "—"),
    document_date: baseFormatters.date,
    starts_at: baseFormatters.date,
    ends_at: baseFormatters.date,
    attachments_count: baseFormatters.number,
  },
  expenses: {
    expense_status: (value, row) => {
      return baseFormatters.computed.expenseStatus(value, row);
    },
    category: (value, row) => {
      return baseFormatters.computed.expenseCategory(value, row);
    },
    "expense_category.name": (value, row) => {
      return baseFormatters.computed.expenseCategory(value, row);
    },
    vehicle_id: vehicleIdFormatter,
    vehicle: (value, row) => {
      return baseFormatters.computed.vehicleObject(value, row);
    },
    counterparty: (value, row) => {
      return baseFormatters.computed.counterpartyObject(value, row);
    },
    payment_source: (value, row) => {
      return baseFormatters.computed.paymentSourceObject(value, row);
    },
    source_type: (value, row) => {
      return baseFormatters.computed.expenseSourceType(value, row);
    },
  },
  repairs: {
    inspection_status: (value, row) => {
      return baseFormatters.computed.repairInspectionStatus(value, row);
    },
    status: repairStatusFormatter,
    performed_at: baseFormatters.datetime,
    total_amount: moneyFormatter,
    paid_amount: moneyFormatter,
    balance_amount: moneyFormatter,
    vehicle: (value, row) => {
      return baseFormatters.computed.vehicleObject(value, row);
    },
    responsible: (value, row) => {
      return baseFormatters.computed.spenderObject(value, row);
    },
    inspector: (value, row) => {
      return baseFormatters.computed.spenderObject(value, row);
    },
    inspected_at: baseFormatters.datetime,
    inspection_method_item: (value, row) => {
      const item = row.inspection_method_item;
      if (!item) return "—";
      return createBadgeWithContrast(item.name, item.color);
    },
    counterparty: (value, row) => {
      return baseFormatters.computed.counterpartyObject(value, row);
    },
    items: repairItemsFormatter,
  },
  company_users: {
    permission_role: (value, row) => {
      const permissionRolesStore = usePermissionRolesStore();
      const roleId = row.permission_role_id;
      if (!roleId) return "—";
      const role = permissionRolesStore.getRoleById(roleId);
      return role?.name || "—";
    },
  },
  "edo-documents": {
    document_date: baseFormatters.date,
    driver_initials: (value) => {
      if (!value) return "—";

      return {
        component: "UserIdentityCard",
        props: {
          name: value,
          compact: true,
        },
        text: value,
      };
    },
    vehicle_registration_number: (value) => {
      if (!value) return "—";

      return {
        component: "VehicleRegNumberBadge",
        props: {
          number: value,
          size: "sm",
        },
        text: value,
      };
    },
    order: (value) => {
      if (!value?.id) return "—";

      return {
        component: "OrderLinkCell",
        props: { order: value },
        text: value.selector_name || `Заказ #${value.id}`,
      };
    },
    integration_link: (value, row) => {
      const link = value || {};
      const label = link.label || row.integration_label;
      const url = link.url || row.integration_url;
      if (!label) return "—";
      if (!url) return label;

      return `<a href="${escapeHtml(url)}" class="entity-link" style="color: var(--color-blue); text-decoration: underline; text-underline-offset: 2px;">${escapeHtml(label)}</a>`;
    },
  },
};

function orderPlanFactCell(value, mode) {
  return {
    component: "OrderPlanFactCell",
    props: { value, mode },
    text: JSON.stringify(value || {}),
  };
}

function orderCompositeCell(value, component) {
  return {
    component,
    props: { value },
    text: JSON.stringify(value || {}),
  };
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function getFormatterByKey(key, entityName) {
  const formatterMap = {
    created_at: baseFormatters.datetime,
    updated_at: baseFormatters.datetime,
    date: baseFormatters.date,
    document_date: baseFormatters.date,
    order_date: baseFormatters.date,
    issued_on: baseFormatters.date,
    order_ids: (value) =>
      Array.isArray(value) ? String(value.length) : "0",
    starts_at: baseFormatters.datetime,
    ends_at: baseFormatters.datetime,
    start_date: baseFormatters.date,
    end_date: baseFormatters.date,
    payment_date: baseFormatters.date,
    spent_at: baseFormatters.date,
    actual_started_at: baseFormatters.date,
    actual_finished_at: baseFormatters.date,
    planned_started_at: baseFormatters.date,
    planned_finished_at: baseFormatters.date,
    actual_pickup_at: baseFormatters.date,
    actual_delivery_at: baseFormatters.date,
    planned_pickup_at: baseFormatters.date,
    planned_delivery_at: baseFormatters.date,
    confirmed_at: baseFormatters.datetime,
    enabled: baseFormatters.status,
    status: baseFormatters.badge,
    is_active: baseFormatters.status,
    price: baseFormatters.big_number,
    amount: baseFormatters.big_number,
    total: baseFormatters.big_number,
    quantity: baseFormatters.number,
    odometer: baseFormatters.number,
    id: baseFormatters.number,
    drivers: baseFormatters.array,
    vehicles: vehicleArrayFormatter,
    documents: baseFormatters.array,
    attachments: baseFormatters.array,
    full_name: baseFormatters.computed.fullName,
    user_phone: baseFormatters.computed.userPhone,
    user_email: baseFormatters.computed.userEmail,
    trip_drivers: baseFormatters.computed.tripDrivers,
    trip_vehicles: baseFormatters.computed.tripVehicles,
    expense_status: baseFormatters.computed.expenseStatus,
    expense_category: baseFormatters.computed.expenseCategory,
    vehicle_id: vehicleIdFormatter,
    vehicle: baseFormatters.computed.vehicleObject,
    counterparty: baseFormatters.computed.counterpartyObject,
    payment_source: baseFormatters.computed.paymentSourceObject,
    spender: baseFormatters.computed.spenderObject,
    source_type: baseFormatters.computed.expenseSourceType,
    order_type: (value, row) => {
      const type = row.order_type;
      if (!type) return "—";
      return createBadgeWithContrast(type.name, type.color);
    },
  };

  if (
    entityName &&
    entitySpecificFormatters[entityName] &&
    entitySpecificFormatters[entityName][key]
  ) {
    return entitySpecificFormatters[entityName][key];
  }

  if (formatterMap[key]) {
    return formatterMap[key];
  }

  if (key.includes(".")) {
    return (value, row) => {
      if (value !== undefined) {
        return baseFormatters.text(value);
      }
      try {
        const path = key.split(".");
        let result = row;
        for (const part of path) {
          if (result === null || result === undefined) break;
          result = result[part];
        }
        return baseFormatters.text(result);
      } catch (error) {
        console.warn(
          `Не удалось получить значение для кастомного поля ${key}:`,
          error
        );
        return "—";
      }
    };
  }

  return baseFormatters.text;
}

function createBadge(value, color, displayText) {
  return `<span style="display: inline-block; padding: 7px; width: max-content; border-radius: 12px; font-size: 13px; line-height: 1.4; color: #fff; background: ${color};">${displayText}</span>`;
}

function createBadgeWithContrast(displayText, bgColor) {
  if (!bgColor) return displayText || "—";
  const textColor = getContrastColor(bgColor);
  return `<span style="display: inline-block; padding: 7px; width: min-content; border-radius: 12px; font-size: 13px; line-height: 1.4; color: ${textColor}; background: ${bgColor};">${displayText}</span>`;
}

/** Собирает видимые колонки в порядке серверной конфигурации. */
export async function createColumnsFromServerConfig(
  { tableName, serverFields, sortFields = [] }
) {
  try {
    return serverFields
      .filter((field) => field.enabled !== false)
      .sort((a, b) => a.position - b.position)
      .map((field) => {
        const formatter = getFormatterByKey(
          field.field_key,
          tableName
        );

        const sortable =
          sortFields.length > 0
            ? sortFields.includes(field.field_key)
            : !field.field_key.includes(".");

        return {
          key: field.field_key,
          label: field.label,
          width: field.width || 150,
          minWidth: field.width || 150,
          sortable,
          formatter: (value, row) =>
            formatter(value, row, { key: field.field_key }),
          isCustom: field.field_key.includes("."),
        };
      });
  } catch (error) {
    console.error(
      `Ошибка создания колонок для таблицы: ${tableName}`,
      error
    );
    return [];
  }
}

/** Загружает колонки из переданной конфигурации или локальной схемы ресурса. */
export async function loadTableColumns(
  tableName,
  serverFields = null
) {
  if (serverFields) {
    return createColumnsFromServerConfig({ tableName, serverFields });
  }

  const schemaColumnsMap = {
    "edo-documents": edoDocumentsColumns,
    vehicle_documents: [
      { key: "vehicle_registration_number", label: "ТС", minWidth: 130 },
      { key: "document_kind", label: "Тип", minWidth: 170 },
      { key: "document_name", label: "Документ", minWidth: 220 },
      { key: "ownership_type", label: "Тип владения", minWidth: 150 },
      { key: "basis_counterparty", label: "Контрагент основания", minWidth: 180 },
      { key: "document_number", label: "Номер", minWidth: 120 },
      { key: "document_date", label: "Дата", minWidth: 110 },
      { key: "starts_at", label: "Действует с", minWidth: 120 },
      { key: "ends_at", label: "Действует до", minWidth: 120 },
      { key: "current", label: "Текущий", minWidth: 110 },
      { key: "attachments_count", label: "Файлы", minWidth: 90 },
    ],
  };

  if (schemaColumnsMap[tableName]) {
    const schemaFields = schemaColumnsMap[tableName].map(
      (field, index) => ({
        field_key: field.key,
        label: field.label,
        position: index,
        enabled: field.enabled !== false,
        width: field.width || field.minWidth || 150,
      })
    );

    return createColumnsFromServerConfig({
      tableName,
      serverFields: schemaFields,
    });
  }

  if (tableName === "expenses") {
    return createColumnsFromServerConfig({ tableName, serverFields: [] });
  }
  return [];
}

function formatValue(value, columnConfig, row) {
  const { formatter } = columnConfig;
  if (formatter && typeof formatter === "function") {
    return formatter(value, row, columnConfig);
  }
  return baseFormatters.text(value);
}

// Публичный набор базовых formatter-ов нужен таблицам с локальной конфигурацией.
export { baseFormatters };
